import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { AnimatePresence } from "framer-motion"
import { toast } from "react-toastify"
import axiosInstance from "../../utils/axios/axiosConfig"
import ProductDetails from "./ProductDetails"
import FilterModal from "./FilterModal"
import { DeleteModal } from '../../components'
import { MagnifyingGlass, Plus, Trash, Eye, PencilSimple, Package, Funnel } from "@phosphor-icons/react"
import {
  PageHeader, Button, Input, Table, THead, TBody, TR, TH, TD,
  Badge, Skeleton, EmptyState, Pagination, Tooltip,
} from "../../components/ui"

function Products() {
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [productId, setProductId] = useState(null)

  const [brands, setBrands] = useState([])
  const [categories, setCategories] = useState([])

  const [filters, setFilters] = useState({
    search: "",
    categories: [],
    brands: [],
    status: [],
    priceRange: { min: 0, max: 1000 },
    isTopSeller: false,
    isFeatured: false,
    isSpecialOffer: false,
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    getProducts()
    getBrands()
    getCategories()
  }, [done])

  const getBrands = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.post(`/brands/search`)
      const responseData = response.data
      if (responseData) {
        setBrands(responseData.data)
      }
      setLoading(false)
    } catch (error) {
      toast.error(error.response.data.message)
      setLoading(false)
    }
  }

  const getCategories = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.post(`/categories/search`)
      const responseData = response.data
      if (responseData) {
        setCategories(responseData.data)
      }
      setLoading(false)
    } catch (error) {
      toast.error(error.response.data.message)
      setLoading(false)
    }
  }

  const getProducts = async (page = 1) => {
    try {
      setLoading(true)
      const response = await axiosInstance.post(`/products/search?page=${page}&per_page=10`, filters)
      const responseData = response.data
      if (responseData) {
        setProducts(responseData.data)
        setCurrentPage(responseData.current_page)
        setTotalPages(responseData.last_page)
      }
      setLoading(false)
    } catch (error) {
      toast.error(error.response.data.message)
      setLoading(false)
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    getProducts(1)
  }

  const openDetails = (product) => {
    setSelectedProduct(product)
    setIsDetailsOpen(true)
  }

  const closeDetails = () => {
    setIsDetailsOpen(false)
    setSelectedProduct(null)
  }



  return (
    <div className="w-full p-6">
      <PageHeader
        title="Products"
        subtitle="Manage your catalog, pricing and inventory."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Search products…"
              value={filters.search}
              onChange={(e) => handleFilterChange({ ...filters, search: e.target.value })}
              containerClassName="w-full sm:w-72"
            />
            <Button variant="outline" icon={Funnel} onClick={() => setIsFilterModalOpen(true)}>
              Filters
            </Button>
            <Button icon={Plus} onClick={() => navigate("/products/new")}>
              Add product
            </Button>
          </>
        }
      />

      <Table>
        <THead sticky>
          <TR className="hover:bg-transparent">
            <TH>Product</TH>
            <TH>SKU</TH>
            <TH>Price</TH>
            <TH>Stock</TH>
            <TH>Status</TH>
            <TH className="text-right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <TR key={i} className="hover:bg-transparent">
                <TD>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-3.5 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                </TD>
                <TD><Skeleton className="h-3.5 w-20" /></TD>
                <TD><Skeleton className="h-3.5 w-16" /></TD>
                <TD><Skeleton className="h-3.5 w-10" /></TD>
                <TD><Skeleton className="h-5 w-16 rounded-full" /></TD>
                <TD><Skeleton className="ml-auto h-7 w-20" /></TD>
              </TR>
            ))}

          {!loading &&
            products.map((product) => (
              <TR key={product.id}>
                <TD>
                  <div className="flex items-center gap-3">
                    <img
                      src={(product.images || [])[0] || "/placeholder.svg"}
                      alt={product.name}
                      className="h-10 w-10 rounded-lg border border-line bg-surface-2 object-cover"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-ink">{product.name}</div>
                      <div className="truncate text-xs text-muted">{product.category?.name || "—"}</div>
                    </div>
                  </div>
                </TD>
                <TD className="text-muted">{product.sku || "—"}</TD>
                <TD className="font-medium">
                  <div className="flex items-center gap-2">
                    <span>
                      {product.has_variants ? (
                        <span className="text-muted">Per variant</span>
                      ) : product.price != null ? (
                        `$${Number.parseFloat(product.price).toFixed(2)}`
                      ) : (
                        "—"
                      )}
                    </span>
                    {product.type === "demander_devis" && <Badge tone="neutral">Devis</Badge>}
                  </div>
                </TD>
                <TD>
                  {product.has_variants
                    ? (product.variants || []).reduce((s, v) => s + Number(v.stock || 0), 0)
                    : product.stock}
                </TD>
                <TD>
                  <Badge tone={product.is_active ? "success" : "neutral"} dot>
                    {product.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TD>
                <TD>
                  <div className="flex items-center justify-end gap-0.5">
                    <Tooltip label="View">
                      <button onClick={() => openDetails(product)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                        <Eye size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip label="Edit">
                      <button onClick={() => navigate(`/products/update/${product.id}`)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                        <PencilSimple size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip label="Delete">
                      <button
                        onClick={() => {
                          setProductId(product.id);
                          setDeleteModal(true);
                        }}
                        className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600"
                      >
                        <Trash size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </TD>
              </TR>
            ))}

          {!loading && products.length === 0 && (
            <tr>
              <td colSpan={6}>
                <EmptyState
                  icon={Package}
                  title="No products found"
                  description="Try changing your search or filters, or add your first product."
                  action={
                    <Button icon={Plus} onClick={() => navigate("/products/new")}>
                      Add product
                    </Button>
                  }
                />
              </td>
            </tr>
          )}
        </TBody>
      </Table>

      <Pagination
        className="mt-4"
        page={currentPage}
        totalPages={totalPages}
        onChange={(p) => getProducts(p)}
        summary={`Page ${currentPage} of ${totalPages}`}
      />

      {/* Drawers / Modals */}
      <AnimatePresence>
        {isDetailsOpen && <ProductDetails product={selectedProduct} onClose={closeDetails} />}
        {isFilterModalOpen && (
          <FilterModal
            filters={filters}
            setFilters={handleFilterChange}
            onClose={() => setIsFilterModalOpen(false)}
            categories={categories}
            brands={brands}
          />
        )}
      </AnimatePresence>

      <DeleteModal
        setDeleteId={setProductId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={productId}
        Model="products"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  );
}

export default Products

